import React, { useCallback, useMemo } from 'react';
import { Check, KeyRound, TriangleAlert } from 'lucide-react';
import {
  DATASET_TRIGGER_CRITERIA,
  DATASET_TRIGGER_CRITERION_DESCRIPTIONS,
  DATASET_TRIGGER_CRITERION_LABELS,
  DATASET_TRIGGER_RUN_MODES,
  DATASET_TRIGGER_RUN_MODE_DESCRIPTIONS,
  DATASET_TRIGGER_RUN_MODE_LABELS,
  criterionTracksValues,
  type DatasetTriggerCriterion,
  type DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  FilterConditionsBuilder,
  type FilterConditionsConfig,
} from '../workflow/components/node/edit-strategies/filter-conditions-builder';
import { VariableSectionPicker } from '../workflow/components/node/edit-strategies/upstream-ref-picker';
import { datasetColumnSections } from '../workflow/components/node/edit-strategies/variable-picker';
import { dottedPathFromRef } from '../workflow/components/node/edit-strategies/variable-picker-data';
import type { DatasetShape } from './hooks';

/** The trigger config the wizard builds and the editor edits. */
export type DatasetTriggerDraft = {
  datasetId: string;
  criterion: DatasetTriggerCriterion;
  /** Null watches every non-internal column, resolved when the trigger runs. */
  trackFields: string[] | null;
  /**
   * Condition tree for `record_first_meets_conditions`. Null while incomplete,
   * which is what blocks the wizard's submit — the server treats an absent
   * filter as matching nothing, so saving one would make a dead trigger.
   */
  filter: FilterConditionsConfig | null;
  processExistingRows: boolean;
  /**
   * Whether the trigger fires on its own. `manual` keeps everything else on the
   * draft meaningful — the criterion still decides which rows a run covers — it
   * just waits to be asked from the dataset instead of firing.
   */
  runMode: DatasetTriggerRunMode;
};

export const createDefaultDatasetTriggerDraft = (
  datasetId: string
): DatasetTriggerDraft => ({
  datasetId,
  criterion: 'new_row',
  trackFields: null,
  filter: null,
  processExistingRows: false,
  runMode: 'automatic',
});

/** Whether the draft is complete enough to save. */
export const datasetTriggerDraftIsComplete = (
  draft: DatasetTriggerDraft
): boolean =>
  draft.criterion !== 'record_first_meets_conditions' || draft.filter !== null;

const RowIdentityNote: React.FC<{ shape: DatasetShape | null }> = ({
  shape,
}) => {
  if (shape === null) {
    return null;
  }
  if (shape.keyColumns.length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Rows in this dataset can't be identified, so a trigger can't tell one
          from another. Re-import it to stamp row identity.
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {shape.keyIsInternal ? (
          <>
            Rows are identified by an internal id. Re-importing this dataset
            makes every row look new — set key columns on the dataset to keep
            identity stable across imports.
          </>
        ) : (
          <>
            Rows are identified by{' '}
            <span className="font-medium text-foreground">
              {shape.keyColumns.join(', ')}
            </span>
            .
          </>
        )}
      </span>
    </div>
  );
};

/**
 * One choice in a radio-style list. Shared by the criterion and the run mode
 * because they are the same decision shape — pick exactly one, and the
 * one-line description is what makes the choice legible.
 */
const OptionCard: React.FC<{
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ label, description, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      'flex items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors',
      selected
        ? 'border-primary bg-primary/5'
        : 'border-transparent hover:bg-muted'
    )}
  >
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{label}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    {selected ? <Check className="mt-1 h-4 w-4 shrink-0 text-primary" /> : null}
  </button>
);

const ColumnToggle: React.FC<{
  name: string;
  selected: boolean;
  onToggle: () => void;
}> = ({ name, selected, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={cn(
      'rounded-full border px-3 py-1 text-xs transition-colors',
      selected
        ? 'border-primary bg-primary/10 text-primary'
        : 'border-input text-muted-foreground hover:bg-muted'
    )}
  >
    {name}
  </button>
);

/**
 * Field picker for a dataset condition: the row's own columns, addressed as JSON
 * pointers so `/email` reads the `email` column at evaluation time. The filter
 * *node* offers upstream-node fields here instead; that is the only difference
 * between the two builders.
 *
 * The same picker the workflow builder uses, rather than the `<Select>` this
 * had: search, sectioning and row layout are one component now, so choosing a
 * field reads the same here as it does three screens away in a filter node.
 */
const DatasetColumnPicker: React.FC<{
  columns: string[];
  value: string;
  onChange: (next: string) => void;
}> = ({ columns, value, onChange }) => {
  const sections = useMemo(
    () => datasetColumnSections({ columns, heading: 'This row' }),
    [columns]
  );
  const chosen = value === '' ? null : dottedPathFromRef(value);
  return (
    <VariableSectionPicker
      sections={sections}
      onPick={onChange}
      placeholder="Search columns…"
    >
      <button
        type="button"
        className="border-input bg-background hover:bg-muted/60 flex h-9 w-full items-center justify-start rounded-md border px-3 text-left text-sm"
      >
        {chosen === null ? (
          <span className="text-muted-foreground">
            {columns.length === 0 ? 'No columns yet' : 'Choose a column'}
          </span>
        ) : (
          chosen
        )}
      </button>
    </VariableSectionPicker>
  );
};

/**
 * The dataset trigger's configuration form.
 *
 * Deliberately identical for every dataset kind — csv, manual, and anything
 * added later. How candidate rows are produced belongs to the dataset, so the
 * kind never shows up here.
 */
export const DatasetTriggerConfig: React.FC<{
  draft: DatasetTriggerDraft;
  onChange: (draft: DatasetTriggerDraft) => void;
  shape: DatasetShape | null;
}> = ({ draft, onChange, shape }) => {
  const columns = useMemo(() => shape?.columns ?? [], [shape]);
  // Null means "everything", but the chips read better fully lit than empty.
  const watched = draft.trackFields ?? columns;

  const renderFieldInput = useCallback(
    ({
      value,
      onChange: onFieldChange,
    }: {
      value: string;
      onChange: (next: string) => void;
    }) => (
      <DatasetColumnPicker
        columns={columns}
        value={value}
        onChange={onFieldChange}
      />
    ),
    [columns]
  );

  // The builder reports on every keystroke and keys its effect on the callback
  // identity, so a fresh closure each render would thrash it. Read the latest
  // props through refs and keep the callback stable.
  const draftRef = React.useRef(draft);
  draftRef.current = draft;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const handleFilterChange = useCallback(
    (filter: FilterConditionsConfig | null) => {
      onChangeRef.current({ ...draftRef.current, filter });
    },
    []
  );

  const toggleColumn = (name: string): void => {
    const next = watched.includes(name)
      ? watched.filter((entry) => entry !== name)
      : [...watched, name];
    onChange({
      ...draft,
      // Back to null once every column is watched, so a column added later is
      // picked up automatically instead of being silently ignored.
      trackFields:
        next.length === columns.length && columns.every((c) => next.includes(c))
          ? null
          : next,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <RowIdentityNote shape={shape} />

      {/* Above the criterion because it changes what the rest of the form
          means: a manual trigger still needs a criterion — that is what decides
          which rows a run covers — but it has nothing to backfill, so that
          option drops away below. */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">
          Run this workflow
        </label>
        <div className="flex flex-col gap-1">
          {DATASET_TRIGGER_RUN_MODES.map((runMode) => (
            <OptionCard
              key={runMode}
              label={DATASET_TRIGGER_RUN_MODE_LABELS[runMode]}
              description={DATASET_TRIGGER_RUN_MODE_DESCRIPTIONS[runMode]}
              selected={draft.runMode === runMode}
              onSelect={() =>
                onChange({
                  ...draft,
                  runMode,
                  // A backfill is a promise to run for every existing row, which
                  // is the one thing a manual trigger has said it will not do on
                  // its own. Cleared here rather than ignored server-side, so
                  // switching back to automatic does not silently restore an
                  // opt-in the user can no longer see.
                  ...(runMode === 'manual' && { processExistingRows: false }),
                })
              }
            />
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">
          {draft.runMode === 'manual' ? 'Run for rows where' : 'Run when'}
        </label>
        <div className="flex flex-col gap-1">
          {DATASET_TRIGGER_CRITERIA.map((criterion) => (
            <OptionCard
              key={criterion}
              label={DATASET_TRIGGER_CRITERION_LABELS[criterion]}
              description={DATASET_TRIGGER_CRITERION_DESCRIPTIONS[criterion]}
              selected={draft.criterion === criterion}
              onSelect={() => onChange({ ...draft, criterion })}
            />
          ))}
        </div>
      </div>

      {criterionTracksValues(draft.criterion) ? (
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            Watch these fields
          </label>
          <p className="text-xs text-muted-foreground">
            {draft.trackFields === null
              ? 'Every column. A change to any of them fires the workflow.'
              : 'Only a change to one of these fires the workflow.'}
          </p>
          {columns.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              This dataset has no columns yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {columns.map((name) => (
                <ColumnToggle
                  key={name}
                  name={name}
                  selected={watched.includes(name)}
                  onToggle={() => toggleColumn(name)}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}

      {draft.criterion === 'record_first_meets_conditions' ? (
        <div className="space-y-1.5">
          {columns.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              This dataset has no columns yet, so there is nothing to match on.
            </p>
          ) : (
            <FilterConditionsBuilder
              initial={draft.filter}
              seedKey={draft.datasetId}
              onChange={handleFilterChange}
              renderFieldInput={renderFieldInput}
              heading="Run the first time a row matches:"
            />
          )}
        </div>
      ) : null}

      {/* Only in automatic mode. A backfill is the trigger firing for every row
          that already exists, which is precisely what "only when I run it" opts
          out of — offering it here would be two settings contradicting each
          other. The manual equivalent is the dataset's own Run menu, which
          covers the same rows on demand. */}
      {draft.runMode === 'automatic' ? (
        <label className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5">
          <input
            type="checkbox"
            checked={draft.processExistingRows}
            onChange={(e) =>
              onChange({ ...draft, processExistingRows: e.target.checked })
            }
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">
              Run for rows that already exist
            </span>
            <span className="block text-xs text-muted-foreground">
              {draft.processExistingRows
                ? 'Every row in the dataset will run through this workflow once.'
                : 'Off: only rows added or changed from now on.'}
            </span>
          </span>
        </label>
      ) : (
        <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
          Nothing runs until you start it. Open the dataset and use the Run
          button in the Automations column to run this over the rows you choose.
        </div>
      )}
    </div>
  );
};
